import React, { useState, useMemo } from 'react';
import { ExternalLink, FileText, Search } from 'lucide-react';
import yojnasData from '../data/yojnas.json';
import { Helmet } from 'react-helmet';

const GovtSchemes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const { schemes, seo } = yojnasData;

  const categories = ['All', ...new Set(schemes.map(s => s.category))];

  const filteredSchemes = useMemo(() => {
    return schemes.filter(scheme => {
      const matchesCategory = selectedCategory === 'All' || scheme.category === selectedCategory;
      const matchesSearch = searchTerm.trim() === '' || 
        scheme.nameHi.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scheme.nameEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scheme.use.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scheme.category.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [schemes, searchTerm, selectedCategory]);

  const CategoryFilter = () => (
    <div className="bg-white p-2 rounded-lg shadow-sm mb-6 sticky top-20 md:top-24 z-10">
      <div className="flex flex-wrap justify-center gap-2">
        {categories.map(category => (
          <button 
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${selectedCategory === category ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-blue-100'}`}>
            {category}
          </button>
        ))}
      </div>
    </div>
  );

  const SchemeCard = ({ scheme }) => (
    <div className="bg-white rounded-xl shadow-md border border-slate-200/80 overflow-hidden transform hover:-translate-y-1 transition-transform duration-200 flex flex-col h-full">
      <div className="bg-blue-50 p-4 border-b border-blue-100">
        <h3 className="font-bold text-lg text-blue-800">{scheme.nameHi}</h3>
        <p className="font-semibold text-blue-600 text-sm">{scheme.nameEn}</p>
      </div>
      <div className="p-5 flex-grow">
        <p className="text-slate-600 text-sm mb-4"><strong>उद्देश्य:</strong> {scheme.use}</p>
        <div className="mb-4">
            <h4 className="font-semibold text-slate-700 mb-2 flex items-center"><FileText size={16} className="mr-2 text-blue-500" />आवश्यक दस्तावेज़:</h4>
            <div className="flex flex-wrap gap-2">
                {scheme.documents.map((doc, i) => (
                    <span key={i} className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">{doc}</span>
                ))}
            </div>
        </div>
      </div>
      <div className="bg-slate-50/70 p-4 border-t border-slate-200 mt-auto">
        <a href={scheme.portal} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm">
          आधिकारिक पोर्टल
          <ExternalLink size={16} className="ml-2" />
        </a>
      </div>
    </div>
  );

  return (
    <div className="w-full py-8">
        <Helmet>
            <title>{seo.metaTitleEn}</title>
            <meta name="description" content={seo.metaDescriptionEn} />
            <meta name="keywords" content={seo.keywords.join(', ')} />
            <meta property="og:title" content={seo.metaTitleEn} />
            <meta property="og:description" content={seo.metaDescriptionEn} />
        </Helmet>

        <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800">{yojnasData.module}</h1>
            <p className="text-slate-600 mt-2 max-w-2xl mx-auto">{seo.metaDescriptionHi}</p>
        </div>

        <div className="mb-8 max-w-2xl mx-auto">
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input 
                    type="search"
                    placeholder="योजना का नाम, श्रेणी, या उपयोग से खोजें..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-full shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
            </div>
        </div>

        <CategoryFilter />

        {filteredSchemes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSchemes.map(scheme => (
                    <SchemeCard key={scheme.nameEn} scheme={scheme} />
                ))}
            </div>
        ) : (
            <div className="text-center py-12">
                <p className="text-slate-500 font-medium">कोई योजना नहीं मिली। कृपया अपना खोज मानदंड बदलें।</p>
            </div>
        )}
    </div>
  );
};

export default GovtSchemes;
